import { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import HeroSection from '../HeroSection/HeroSection';
import FlightDetailsPage from '../../pages/FlightDetailsPage/FlightDetailsPage';
import AddPassengersPage from '../../pages/AddPassengersPage/AddPassengersPage';
import PaymentPage from '../../pages/PaymentPage/PaymentPage';
import ReturnFlightDetails from '../ReturnFlightDetails/ReturnFlightDetails';
import MultiCityFlightDetails from '../MultiCityFlightDetails/MultiCityFlightDetails';
import SessionTimeoutWarningModal from '../Auth/SessionTimeoutWarningModal';
import { mergeBookingData, serializeBookingData } from '../../utils/bookingQueryParams';
import bookingService from '../../services/bookingService';
import { useSessionValidityGate } from '../../hooks/useSessionValidityGate';

const FlightBookingFlow = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Merge booking data from URL, state, and session
  const bookingData = mergeBookingData(location);

  // Get flight ID from URL params or location state
  const flightId = params.id || location.state?.selectedFlight?.id || bookingData.flight_id;
  const returnFlightId = location.state?.returnFlightId || location.state?.selectedFlights?.[1]?.id || bookingData.return_flight_id;
  const selectedFlights = location.state?.selectedFlights || bookingData.selectedFlights || [];
  const passengers = bookingData.passengers || 1;
  const routeType = location.state?.routeType || location.state?.searchData?.tripType || bookingData.routeType || 'One Way';
  const fromUrl = location.state?.fromUrl;

  // Determine initial step - check for preserved step first, then pricing/passengerData
  const preservedStep = bookingData.currentStep || sessionStorage.getItem('currentBookingStep');
  const initialStep = preservedStep || (location.state?.pricing || bookingData.passengerData ? 'passengers' : 'details');
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [hasPassengerData, setHasPassengerData] = useState(false);

  const {
    showSessionTimeoutWarning,
    sessionValidity,
    gateProceed,
    handleStaySignedInAndProceed,
    handleLogoutFromWarning
  } = useSessionValidityGate('FlightBookingFlow');

  const sessionModalMessages = {
    continueMessage:
      'To continue your booking securely, we need to refresh your session. Click "Stay Signed In" to continue, or you\'ll be logged out.',
    successToastMessage: 'Session extended. You can continue with your booking.'
  };
  
  // Clear preserved step if we're starting fresh (no preserved data and no passenger data)
  useEffect(() => {
    if (!preservedStep && !bookingData.passengerData && !location.state?.pricing) {
      sessionStorage.removeItem('currentBookingStep');
      console.log('🧹 Cleared currentBookingStep for fresh booking');
    }
  }, []);
  
  // Check for saved passenger data in sessionStorage to enable Payment tab
  useEffect(() => {
    const checkPassengerData = () => {
      // Check if we have passenger data in bookingData (already submitted)
      if (bookingData.passengerData && bookingData.passengerData.length > 0) {
        setHasPassengerData(true);
        return;
      }
      
      // Check sessionStorage for auto-saved passenger data
      try {
        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        const userId = user.id || 'guest';
        const passengerDataKey = `passenger_form_data_${userId}_${flightId}`;
        const savedFormData = sessionStorage.getItem(passengerDataKey);
        
        if (savedFormData) {
          const parsed = JSON.parse(savedFormData);
          // Check if data is recent (within 24 hours) and has passengers
          const twentyFourHours = 24 * 60 * 60 * 1000;
          const age = Date.now() - parsed.timestamp;
          
          if (age < twentyFourHours && parsed.passengers && parsed.passengers.length > 0) {
            // Validate that at least the first passenger has required fields filled
            const firstPassenger = parsed.passengers[0];
            const hasRequiredFields = firstPassenger.first_name && 
                                     firstPassenger.last_name && 
                                     firstPassenger.date_of_birth;
            
            setHasPassengerData(hasRequiredFields);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking passenger data:', error);
      }
      
      setHasPassengerData(false);
    };
    
    checkPassengerData();
    
    // Re-check every 2 seconds while on passengers page to detect form changes
    const interval = currentStep === 'passengers' ? setInterval(checkPassengerData, 2000) : null;
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [flightId, bookingData.passengerData, currentStep]);
  
  // Save current step to sessionStorage whenever it changes
  const updateCurrentStep = (step) => {
    setCurrentStep(step);
    sessionStorage.setItem('currentBookingStep', step);
  };

  const tabs = [
    { id: 'home', label: 'Home', number: '1' },
    { id: 'flights', label: 'Flights', number: '2' },
    { id: 'details', label: 'Details', number: '3' },
    { id: 'passengers', label: 'Passengers', number: '4' },
    { id: 'payment', label: 'Payment', number: '5' }
  ];

  const handleTabClick = (tabId) => {
    if (tabId === 'home') {
      // Get search parameters from sessionStorage or URL
      const searchParams = sessionStorage.getItem('flight-search-params');
      if (searchParams) {
        navigate(`/search-flight?${searchParams}`);
      } else {
        navigate('/search-flight');
      }
    } else if (tabId === 'flights') {
      const flightListUrl = sessionStorage.getItem('flight-list-Url') || '/one-way-flight';
      navigate(flightListUrl);
    } else {
      // Prevent skipping steps - validate that previous steps are completed
      if (tabId === 'passengers') {
        // Can only go to passengers if details step is completed (flight is selected)
        if (!flightId && selectedFlights.length === 0) {
          console.log('❌ Cannot skip to passengers - no flight selected');
          return;
        }
      } else if (tabId === 'payment') {
        // Can only go to payment if passengers step is completed (either submitted or saved in sessionStorage)
        if (!hasPassengerData) {
          console.log('❌ Cannot skip to payment - passengers not added');
          return;
        }
        
        // If passenger data is not in bookingData but exists in sessionStorage, load it
        if (!bookingData.passengerData || bookingData.passengerData.length === 0) {
          console.log('📦 Loading passenger data from sessionStorage before navigating to payment');
          try {
            const user = JSON.parse(sessionStorage.getItem('user') || '{}');
            const userId = user.id || 'guest';
            const passengerDataKey = `passenger_form_data_${userId}_${flightId}`;
            const savedFormData = sessionStorage.getItem(passengerDataKey);
            
            if (savedFormData) {
              const parsed = JSON.parse(savedFormData);
              
              // Update URL with passenger data
              const updatedBookingData = {
                ...bookingData,
                passengerData: parsed.passengers,
                passengers: parsed.passengers.length,
                saveForFuture: parsed.saveForFuture || false,
                bookingOnBehalfOf: parsed.bookingOnBehalfOf || false
              };
              
              const queryString = serializeBookingData(updatedBookingData);
              const newUrl = `/flight-booking-flow/${flightId}?${queryString}`;
              
              console.log('🔗 Navigating to payment with loaded passenger data:', newUrl);
              
              navigate(newUrl, {
                replace: true,
                state: {
                  ...updatedBookingData,
                  fromPassengers: true
                }
              });
            }
          } catch (error) {
            console.error('❌ Error loading passenger data:', error);
          }
        }
      }
      
      // Revoke seat lock if navigating away from payment step
      if (currentStep === 'payment' && (tabId === 'passengers' || tabId === 'details')) {
        if (bookingData.booking_token) {
          console.log('🔓 Revoking seat lock before switching tab...');
          bookingService.revokeLock(bookingData.booking_token).catch(err => {
            console.warn('⚠️ Failed to revoke lock:', err);
          });
        }
      }

      if (tabId === 'payment') {
        gateProceed(() => updateCurrentStep('payment'));
        return;
      }

      updateCurrentStep(tabId);
    }
  };

  const handleDetailsNext = () =>
    gateProceed(() => {
      updateCurrentStep('passengers');
    });

  const handlePassengersNext = (data) => {
    console.log('📝 FlightBookingFlow: handlePassengersNext called with data:', data);

    // Update the URL with passenger data so PaymentPage can access it
    const updatedBookingData = {
      ...bookingData,
      passengerData: data.passengers,
      passengers: data.totalPassengers,
      routeType: data.routeType,
      pricing: data.pricing,
      saveForFuture: data.saveForFuture,
      farePerPax: data.pricing?.farePerPax,
      totalFare: data.pricing?.totalFare,
      blockFare: data.pricing?.blockFare,
      amount: data.pricing?.isSeatPricing ? data.pricing?.totalFare : data.pricing?.blockFare,
      // Preserve selectedFlights data for multi-leg bookings
      selectedFlights: bookingData.selectedFlights || selectedFlights,
      // Include the seat lock token
      booking_token: data.booking_token
    };

    console.log('📦 FlightBookingFlow: Updated booking data:', updatedBookingData);

    // Serialize booking data to query string
    const queryString = serializeBookingData(updatedBookingData);
    const newUrl = `/flight-booking-flow/${flightId}?${queryString}`;

    console.log('🔗 FlightBookingFlow: Navigating to:', newUrl);

    // Use React Router's navigate to update URL and location state
    navigate(newUrl, {
      replace: true,
      state: {
        ...updatedBookingData,
        fromPassengers: true
      }
    });

    console.log('🔄 FlightBookingFlow: Setting current step to payment');
    updateCurrentStep('payment');
    console.log('✅ FlightBookingFlow: Current step updated to payment');
  };

  return (
    <div className="w-full">
      <SessionTimeoutWarningModal
        isOpen={showSessionTimeoutWarning}
        remainingTime={sessionValidity}
        onStaySignedIn={handleStaySignedInAndProceed}
        onLogout={handleLogoutFromWarning}
        continueMessage={sessionModalMessages.continueMessage}
        successToastMessage={sessionModalMessages.successToastMessage}
      />
      {/* Hero Section */}
      <HeroSection />

      {/* Tabs Navigation */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex flex-wrap justify-center mx-auto gap-2 w-full py-4 px-4">
          {tabs.map((tab) => {
            // Determine if tab should be disabled
            let isDisabled = false;
            if (tab.id === 'passengers') {
              isDisabled = !flightId && selectedFlights.length === 0;
            } else if (tab.id === 'payment') {
              isDisabled = !hasPassengerData;
            }
            
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                disabled={isDisabled}
                className={`no-underline focus:outline-none transition-all ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <div className="mr-4">
                  <div
                    className={`w-32 h-10 border-b-2 pr-7 pl-0.5 flex items-center justify-center gap-2 transition-all ${
                      isDisabled 
                        ? 'border-gray-300 cursor-not-allowed' 
                        : currentStep === tab.id
                          ? 'border-gray-400 cursor-pointer'
                          : 'border-gray-400 hover:border-gray-500 cursor-pointer'
                    }`}
                  >
                    <span
                      className={`text-2xl font-semibold ${
                        isDisabled 
                          ? 'text-gray-400' 
                          : currentStep === tab.id 
                            ? 'text-yellow-400' 
                            : 'text-gray-600'
                      }`}
                    >
                      {tab.number}
                    </span>
                    <span
                      className={`text-xs font-medium ${
                        isDisabled 
                          ? 'text-gray-400' 
                          : 'text-gray-600'
                      }`}
                    >
                      {tab.label}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Booking Flow Content */}
      <div className="w-full bg-gray-50">
        {console.log('🎬 FlightBookingFlow rendering, currentStep:', currentStep, 'flightId:', flightId, 'routeType:', routeType)}

        {/* Multi-City Flight Details */}
        {currentStep === 'details' && routeType === 'Multicity' && selectedFlights.length > 0 && (
          <MultiCityFlightDetails
            selectedFlights={selectedFlights}
            passengers={passengers}
            onNext={handleDetailsNext}
          />
        )}

        {/* Return Flight Details */}
        {currentStep === 'details' && (routeType === 'Return' || routeType === 'Returns') && flightId && (
          <ReturnFlightDetails
            outboundFlightId={flightId}
            returnFlightId={returnFlightId || flightId}
            passengers={passengers}
            onNext={handleDetailsNext}
          />
        )}

        {/* One-Way Flight Details */}
        {currentStep === 'details' && routeType !== 'Return' && routeType !== 'Returns' && routeType !== 'Multicity' && flightId && (
          <FlightDetailsPage
            id={flightId}
            passengers={passengers}
            routeType={routeType}
            fromUrl={fromUrl}
            hideBreadcrumb={true}
            onNext={handleDetailsNext}
          />
        )}

        {currentStep === 'details' && !flightId && selectedFlights.length === 0 && (
          <div className="px-4 md:px-8 py-8">
            <div className="max-w-6xl mx-auto">
              <p className="text-red-600 font-semibold">Error: No flight selected. Please go back and select a flight.</p>
            </div>
          </div>
        )}

        {currentStep === 'passengers' && (routeType === 'Multicity' ? selectedFlights.length > 0 : flightId) && (
          <AddPassengersPage
            id={flightId}
            selectedFlights={
              routeType === 'Multicity' || routeType === 'Return' || routeType === 'Returns'
                ? selectedFlights
                : undefined
            }
            routeType={routeType}
            onNext={handlePassengersNext}
            onBack={() => updateCurrentStep('details')}
          />
        )}

        {currentStep === 'passengers' && !flightId && (
          <div className="px-4 md:px-8 py-8">
            <div className="max-w-6xl mx-auto">
              <p className="text-red-600 font-semibold">Error: No flight selected. Please go back and select a flight.</p>
            </div>
          </div>
        )}

        {currentStep === 'payment' && flightId && (
          <PaymentPage id={flightId} hideBreadcrumb={true} onBack={() => updateCurrentStep('passengers')} />
        )}

        {currentStep === 'payment' && !flightId && (
          <div className="px-4 md:px-8 py-8">
            <div className="max-w-6xl mx-auto">
              <p className="text-red-600 font-semibold">Error: No flight selected. Please go back and select a flight.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightBookingFlow;
